'use client';

import { Button } from '@jouzen/ecom-components/v3/Button';
import { FormControl } from '@jouzen/ecom-components/v3/FormControl';
import { setCookie } from '@jouzen/ecom-utils';
import { useFormik } from 'formik';
import { useTranslations } from 'next-intl';
import { Dispatch, SetStateAction, useState } from 'react';
import * as Yup from 'yup';

import { trackAnalytics } from '@/analytics/track';
import { EventType } from '@/analytics/types';
import {
  getEmailToken,
  setEmailTokenCookie,
} from '@/app/utils/emailTokenHelper';
import { type Partner } from '@/configs/partner';
import { shouldAuthenticate } from '@/utils/auth/shouldAuthenticate';
import { logToDataDog } from '@/utils/logToDatadog';
import { metricIncrement } from '@/utils/reportMetrics';

import SubmitButton from '../SubmitButton';
import { CheckStatus, LOGIN_METRIC_NAME, MetricStep } from './const';

interface EmailFormProps {
  readonly setCheckStatus: Dispatch<SetStateAction<CheckStatus | null>>;
  readonly shouldResetForm: boolean;
  readonly step: any;
  readonly submitForm: (
    formState: null | undefined,
    url: any,
    method: string | undefined,
  ) => Promise<void>;
  readonly error?: string | null;
  readonly partner: Partner;
  readonly isReady: boolean;
}

const EmailForm = ({
  setCheckStatus,
  shouldResetForm,
  step,
  submitForm,
  error,
  partner,
  isReady,
}: EmailFormProps): JSX.Element => {
  const t = useTranslations();
  const [loading, setLoading] = useState<boolean>(false);
  const handleSubmit = async (values: any) => {
    setLoading(true);
    await metricIncrement(LOGIN_METRIC_NAME, {
      step: MetricStep.EMAIL_SUBMITTED,
      partner,
    });

    await setEmailTokenCookie(values?.username);

    // FOR TESTING PURPOSES ONLY
    if (!shouldAuthenticate(values?.username)) {
      setCheckStatus(CheckStatus.SUCCESS_EMAIL);
      setCookie('fake_session', values?.username, 60);
      await metricIncrement(LOGIN_METRIC_NAME, {
        step: MetricStep.EMAIL_SUCCESS,
        partner,
      });
    } else {
      const { model } = step.haapiResponse.actions[0];
      await submitForm(values, model.href, model.method)
        .then(async () => {
          setCheckStatus(CheckStatus.SUCCESS_EMAIL);
          await metricIncrement(LOGIN_METRIC_NAME, {
            step: MetricStep.EMAIL_SUCCESS,
            partner,
          });
        })
        .catch(async (error) => {
          await metricIncrement(LOGIN_METRIC_NAME, {
            step: MetricStep.EMAIL_ERROR,
            partner,
            error,
          });
          await logToDataDog('auth', 'EOP Email Form error', {
            partner,
            error: JSON.stringify(error),
            emailToken: getEmailToken(),
          });
          setCheckStatus(CheckStatus.ERROR);
        });
    }
  };

  const formik = useFormik({
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: () =>
      Yup.object().shape({
        username: Yup.string()
          .trim()
          .required(() => t('address_form_required'))
          .email(() => t('checkout_form_emailformat')),
      }),
    initialValues: {
      username: '',
    },
    onSubmit: handleSubmit,
  });

  const handleResetForm = () => {
    setCheckStatus(null);
    formik.resetForm();
  };

  const trackSubmitClick = async (): Promise<void> => {
    await trackAnalytics(EventType.CTAClicked, {
      cta: 'get code',
      action: 'get code',
      location: 'body',
      path: '/email',
    });
  };

  if (shouldResetForm) {
    return (
      <Button
        variant="outlined-dark"
        data-testid="button-reset-form"
        onClick={handleResetForm}
      >
        {t('my_account_another_email')}
      </Button>
    );
  }

  return (
    <form
      className="flex w-full flex-col gap-2"
      onSubmit={formik.handleSubmit}
      noValidate
    >
      <FormControl
        className="w-full"
        control="TextInput"
        errorMessage={formik.errors?.username ?? error}
        label={t('my_account_email')}
        name="username"
        onChange={formik.handleChange}
        required
        value={formik.values?.username}
      />

      <SubmitButton
        full
        type="submit"
        loading={loading}
        onClick={trackSubmitClick}
        disabled={!isReady}
      >
        {t('eop_get_code_button')}
      </SubmitButton>
    </form>
  );
};

export default EmailForm;
